import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from '../ToastMessages/ToastMessageProvider';
import { getUserEntity } from 'common/UserEntityProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import InvoicesNavbar from '../common/InvoicesNavbar/InvoicesNavbar';
import InvoicesListAccountsPayable from './InvoicesListAccountsPayable';
import { handleAPIError } from 'common/errorHandler';
import { formatDateFilterInvoices } from '../../common/utils';
import ErrorSummary from 'components/common/ErrorSummary';
import { DateRange, ENTITY_TYPE } from 'common/constants';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { GridSortModel } from '@mui/x-data-grid';
import { Card } from '@mui/material';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

export type InvoicesInput = {
  to: string | null;
  number: string | null;
  date: string | null;
  from: string | null;
  range: DateRange | undefined;
  amount: number | null;
  amounts: object;
  idInvoiceType: number | null;
  idWorkflowState: number | null;
  queryTerm?: string | null;
  currentPage?: number;
  sortValue?: string | null;
  orderValue?: string | null;
};

export type ValidatingInput = {
  idInvoiceType: number | null;
  idWorkflowState: number | null;
  queryTerm?: string | null;
  currentPage?: number;
  sortValue?: string | null;
  orderValue?: string | null;
};

export type SearchResult = {
  totalRecords: number | null;
  queryResult: any;
};
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const InvoicesAccountsPayable = () => {
  const navigate = useNavigate();
  const [validatingInput, setValidatingInput] = useState<ValidatingInput | null>({
    idInvoiceType: null,
    idWorkflowState: null,
    queryTerm: '',
    currentPage: 1,
    sortValue: '',
    orderValue: 'asc',
  });

  const [input, setInput] = useState<InvoicesInput | null>({
    to: '',
    number: '',
    date: '',
    from: '',
    range: undefined,
    amount: null,
    amounts: {},
    idInvoiceType: null,
    idWorkflowState: null,
    queryTerm: '',
    currentPage: 1,
    sortValue: '',
    orderValue: 'asc',
  });

  // Local state for search inputs
  const [validatingQueryString, setValidatingQueryString] = useState<string>('');
  const [allInvoicesQueryString, setAllInvoicesQueryString] = useState<string>('');

  useEffect(() => {
    if (input.from && input.to) {
      setInput((prev) => ({
        ...prev,
        range: {
          from: new Date(input.from),
          to: new Date(input.to),
        },
      }));
    }
  }, [input.from, input.to]);

  const [invoicesOwner, setInvoicesOwner] = useState<SearchResult | null>({
    totalRecords: null,
    queryResult: null,
  });

  const [validatingInvoices, setValidatingInvoices] = useState<SearchResult | null>({
    totalRecords: null,
    queryResult: null,
  });

  const [invoiceStatus, setInvoiceStatus] = useState<SearchResult | null>({
    totalRecords: null,
    queryResult: null,
  });
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [loading, setLoading] = useState<boolean>(false);

  const [errors, setErrors] = useState({});

  const api: Api = useApi();

  const toast = useToastMessageQueue();

  const entityType = getUserEntity().entityType;

  const [paginationModel, setPaginationModel] = usePaginationPreference('invoicesAccountsPayable', 10);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'contactName', sort: 'asc' },
  ]);

  useEffect(() => {
    setInput((prev) => ({
      ...prev,
      currentPage: paginationModel.page + 1,
      sortValue: sortModel[0]?.field || '',
      orderValue: sortModel[0]?.sort || 'asc',
    }));
  }, [paginationModel, sortModel]);

  const [validatingLimit, setValidatingLimit] = useState<number>(() => {
    return parseInt(localStorage.getItem('itemsPerPageInvoicesValidating'), 10) || 10;
  });

  useEffect(() => {
    setValidatingLimit(parseInt(localStorage.getItem('itemsPerPageInvoicesValidating'), 10) || 10);
  }, []);

  const fetchInvoicesOwner = (
    term?: string,
    pageNum?: number,
    pageSize?: number,
    sort?: string,
    order?: string,
    f?: Array<string>
  ) => {
    setLoading(true);
    if (input.idWorkflowState) {
      f = [`idworkflowstate:${input.idWorkflowState}`];
    }
    if (input.range?.from) {
      let from = formatDateFilterInvoices(input.range.from);
      f = f || [];
      f.push(`startdate:${from}`);
    }
    if (input.range?.to) {
      let to = formatDateFilterInvoices(input.range.to);
      f = f || [];
      f.push(`enddate:${to}`);
    }
    if (Number(input.amounts['min'])) {
      f = f || [];
      f.push(`amountabove:${Number(input.amounts['min'])}`);
    }
    if (Number(input.amounts['max'])) {
      f = f || [];
      f.push(`amountbelow:${Number(input.amounts['max'])}`);
    }
    (async () => {
      try {
        const response = await api.invoiceApi.apiVversionInvoiceSearchGet(
          '1',
          input.queryTerm,
          input.currentPage,
          paginationModel?.pageSize,
          input.sortValue,
          input.orderValue,
          f,
          {}
        );
        if (response.data.data.queryResult) {
          setInvoicesOwner({
            totalRecords: response.data?.data?.totalRecords,
            queryResult: response.data?.data?.queryResult,
          });
        } else {
          setInvoicesOwner({
            totalRecords: null,
            queryResult: null,
          });
        }
      } catch (error) {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      } finally {
        setLoading(false);
      }
    })();
  };

  const fetchInvoicesStatus = async () => {
    try {
      setLoading(true);
      const response = await api.workflowApi.apiVversionModuleWorkflowStatesModuleIdGet(4, '1');
      if (response.data.data.queryResult) {
        setInvoiceStatus({
          totalRecords: response.data?.data?.totalRecords,
          queryResult: response.data?.data?.queryResult,
        });
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  const fetchValidatingInvoices = async (
    moduleId?: number,
    term?: string,
    pageNum?: number,
    pageSize?: number,
    order?: string,
    f?: Array<string>
  ) => {
    if (validatingInput.idWorkflowState) {
      f = [`idworkflowstate:${validatingInput.idWorkflowState}`];
    }
    try {
      setLoading(true);
      const response = await api.invoiceApi.apiVversionInvoiceSearchIncomingGet(
        '1',
        4,
        validatingInput.queryTerm,
        validatingLimit,
        validatingInput.orderValue
      );

      if (response.data.data.queryResult) {
        setValidatingInvoices({
          totalRecords: response.data?.data?.totalRecords,
          queryResult: response.data?.data?.queryResult,
        });
      } else {
        setValidatingInvoices({
          totalRecords: null,
          queryResult: null,
        });
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }
  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  useEffect(() => {
    fetchInvoicesOwner();
    fetchInvoicesStatus();
  }, [input, paginationModel?.pageSize]);

  useEffect(() => {
    fetchValidatingInvoices();
  }, [validatingInput, validatingLimit]);

  const onSearch = useCallback((queryString, idStatus, range, amounts) => {
    setErrors([]);
    setAllInvoicesQueryString(queryString); // Keep the query string in local state
    setInput((prev) => ({
      ...prev,
      queryTerm: queryString,
      idWorkflowState: idStatus,
      range: range,
      currentPage: 1,
      amounts: amounts,
    }));
  }, []);

  const onSearchValidating = useCallback((queryString, idStatus, range, amounts) => {
    setErrors([]);
    setValidatingQueryString(queryString); // Keep the query string in local state
    setValidatingInput((prev) => ({
      ...prev,
      queryTerm: queryString,
      idWorkflowState: idStatus,
      currentPage: 1,
    }));
  }, []);

  const path = window.location.pathname.split('/').pop();
  const getTitle = () => {
    if (path === 'accountspayable') {
      return intl.get('mainNavigation.billing.accountsPayable');
    }
    return intl.get('mainNavigation.invoice');
  };
  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <div className="card mt-4">
          <div className="card-header">
            {
              <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
                {getTitle()}
              </Typography>
            }
          </div>
          <div className="card-body">
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                }}
              >
                <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VisibilityIcon fontSize="small" />
                        {intl.get('mainNavigation.validatingInvoices')}
                      </Box>
                    }
                    {...a11yProps(0)}
                  />
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarMonthIcon fontSize="small" />
                        {intl.get('mainNavigation.allInvoices')}
                      </Box>
                    }
                    {...a11yProps(1)}
                  />
                </Tabs>

                {(entityType !== ENTITY_TYPE.CORPORATE_ENTITY || path === 'invoicing') &&
                  entityType !== ENTITY_TYPE.CLIENT && (
                    <Box>
                      <Button
                        onClick={() =>
                          entityType === ENTITY_TYPE.CORPORATE_ENTITY
                            ? navigate(`/invoicing/newinvoice`)
                            : navigate(`/myInvoices/upload`)
                        }
                        sx={{ minWidth: 40, height: 40 }}
                      >
                        <AddIcon /> {intl.get('invoice.new') ? intl.get('invoice.new') : intl.get('button.new')}
                      </Button>
                    </Box>
                  )}
              </Box>

              <CustomTabPanel value={value} index={0}>
                <InvoicesNavbar
                  invoiceStatus={validatingInvoices?.queryResult}
                  allInvoices={false}
                  onSearch={onSearchValidating}
                  queryString={validatingQueryString}
                  setQueryString={setValidatingQueryString}
                />
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    <div className="col-md-12">
                      <ErrorSummary errors={errors} showEverything={false} mode="warning" />
                    </div>
                    <InvoicesListAccountsPayable
                      searchResult={validatingInvoices}
                      isValidating={true}
                      loading={loading}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      sortModel={sortModel}
                      onSortModelChange={setSortModel}
                      WorkflowModuleId={5}
                    />
                  </>
                )}
              </CustomTabPanel>

              <CustomTabPanel value={value} index={1}>
                <InvoicesNavbar
                  invoiceStatus={invoiceStatus?.queryResult}
                  onSearch={onSearch}
                  allInvoices={true}
                  queryString={allInvoicesQueryString}
                  setQueryString={setAllInvoicesQueryString}
                />
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    <div className="col-md-12">
                      <ErrorSummary errors={errors} showEverything={false} mode="warning" />
                    </div>
                    <InvoicesListAccountsPayable
                      searchResult={invoicesOwner}
                      isValidating={false}
                      loading={loading}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      sortModel={sortModel}
                      onSortModelChange={setSortModel}
                      WorkflowModuleId={4}
                    />
                  </>
                )}
              </CustomTabPanel>
            </Box>
          </div>
        </div>
      </Box>
    </Card>
  );
};

export default InvoicesAccountsPayable;
